import React from 'react';
import { Text as RNText, TextProps } from 'react-native';

/**
 * Ceiling on how far iOS Dynamic Type / Android font-scale may grow our
 * text.
 *
 * Why a cap is needed at all: React Native's `<Text>` scales with the
 * OS text-size setting by default, but `lineHeight` in a StyleSheet does
 * NOT — it is a fixed point value. So a style like
 *
 *     { fontSize: ms(16, 0.3), lineHeight: ms(21, 0.3) }
 *
 * renders 24pt glyphs inside a 21pt line box once the user picks a 1.5x
 * text size. Lines overlap, descenders clip, and text escapes fixed-size
 * containers. This is what a beta tester saw on the dashboard in August
 * 2026 while it looked perfect on every device we owned — the bug lives
 * in an accessibility setting, not in a screen size.
 *
 * 1.3 is chosen to stay well inside the ~1.35x headroom that our
 * tightest fontSize/lineHeight pairs allow, while still honouring most
 * of the range users actually pick. Do NOT "fix" layout bugs by setting
 * `allowFontScaling={false}` — that ignores the setting entirely and
 * makes the app unusable for people who need large text.
 *
 * The real fix is to stop hardcoding `lineHeight` alongside a scaled
 * `fontSize`. Until that lands everywhere, this cap bounds the damage.
 */
export const MAX_FONT_SIZE_MULTIPLIER = 1.3;

type AppTextProps = TextProps & {
  // React 19 passes `ref` as an ordinary prop to function components,
  // so no forwardRef wrapper is needed — but TextProps doesn't declare
  // it, hence the explicit widening.
  ref?: React.Ref<RNText>;
};

/**
 * Drop-in replacement for react-native's `<Text>` that applies
 * {@link MAX_FONT_SIZE_MULTIPLIER} unless the caller overrides it.
 *
 * Intended to be imported *aliased*, so adopting it on a screen is a
 * one-line change rather than an edit at every call site:
 *
 *     import { AppText as Text } from '../../components/AppText';
 *
 * An explicit `maxFontSizeMultiplier` prop still wins — pass a higher
 * value on screens whose layout genuinely absorbs it.
 */
export function AppText({ maxFontSizeMultiplier, ...rest }: AppTextProps) {
  return (
    <RNText
      maxFontSizeMultiplier={maxFontSizeMultiplier ?? MAX_FONT_SIZE_MULTIPLIER}
      {...rest}
    />
  );
}

export default AppText;
